import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import NotFound404 from 'screens/NotFound404/NotFound404';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { dataSentence, formatUpdated, mostWantedItemList, seoDescriptionFrom } from 'components/SeoTradeData/SeoTradeData';
import { RankedCardRow } from 'components/RankedCardList/RankedCardRow';
import { SeoCardRow, useGetSeoFormatQuery } from 'store/services/seo/publicSeoApi';
import { FORMAT_INTRO_COPY, FORMAT_INTRO_SUFFIX, FORMAT_PAGE_KEYS, FORMAT_PAGE_LABELS, FormatPageKey } from 'constants/formats';
import './Formats.css';

const SITE = 'https://trademagic.gg';
/** Rows shown before "Show all". All rows are in the DOM from the start. */
export const FORMAT_LIST_PREVIEW_ROWS = 12;

const isFormatPageKey = (key: string): key is FormatPageKey => (FORMAT_PAGE_KEYS as readonly string[]).includes(key);

type TabKey = 'wanted' | 'traded' | 'available';

interface TabSpec {
  key: TabKey;
  label: string;
  note?: string;
  rows: SeoCardRow[];
  countLabel: (n: number) => string;
  emptyTitle: string;
  emptyBody: string;
}

const RankedTabPanel: React.FC<{ tab: TabSpec; active: boolean; label: string }> = ({ tab, active }) => {
  const [showAll, setShowAll] = useState(false);
  const hiddenRows = Math.max(0, tab.rows.length - FORMAT_LIST_PREVIEW_ROWS);
  return (
    <div
      role="tabpanel"
      id={`format-tabpanel-${tab.key}`}
      aria-labelledby={`format-tab-${tab.key}`}
      hidden={!active}
      className="formats-tabpanel"
      data-testid={`format-tabpanel-${tab.key}`}
    >
      {tab.rows.length === 0 ? (
        <div className="formats-empty" data-testid="formats-empty">
          <p className="formats-empty__title">{tab.emptyTitle}</p>
          <p className="formats-empty__body">{tab.emptyBody}</p>
          <Link to="/sendcards" className="formats-btn formats-btn--primary">Send Cards</Link>
        </div>
      ) : (
        <>
          <ol className="formats-rows">
            {tab.rows.map((row, i) => (
              <RankedCardRow
                key={`${row.setCode}-${row.collectorNumber}-${i}`}
                rank={i + 1}
                row={row}
                countLabel={tab.countLabel}
                hidden={!showAll && i >= FORMAT_LIST_PREVIEW_ROWS}
              />
            ))}
          </ol>
          {hiddenRows > 0 && !showAll && (
            <button type="button" className="formats-btn formats-btn--ghost formats-show-all" onClick={() => setShowAll(true)}>
              Show all {tab.rows.length}
            </button>
          )}
        </>
      )}
    </div>
  );
};

/**
 * /formats/{formatKey} (Asana 1219902842494504): breadcrumb, "Most wanted
 * {Format} cards", the intro copy, two CTAs, a stats strip, the eight format
 * pills and the three lists as tabs inside one panel, every row in the DOM
 * regardless of the active tab. Title, description, canonical, ItemList JSON-LD,
 * the lowercase redirect and the 404 for unknown keys are unchanged from Asana
 * 1219287567650303.
 */
const FormatDetailPage: React.FC = () => {
  const { formatKey: rawKey = '' } = useParams<{ formatKey: string }>();
  const formatKey = rawKey.toLowerCase();
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('wanted');

  useEffect(() => {
    if (rawKey && rawKey !== formatKey) navigate(`/formats/${formatKey}`, { replace: true });
  }, [rawKey, formatKey, navigate]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setIsMobileMenuOpen(false);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const known = isFormatPageKey(formatKey);
  const { data, isLoading, error } = useGetSeoFormatQuery(formatKey, { skip: !known });

  if (!known) return <NotFound404 />;

  const label = FORMAT_PAGE_LABELS[formatKey];
  const name = data?.name || label;
  const canonical = `${SITE}/formats/${formatKey}`;
  const intro = `${FORMAT_INTRO_COPY[formatKey]} ${FORMAT_INTRO_SUFFIX}`;
  // The data sentence is the meta description only; it no longer renders as body text.
  const description = data ? seoDescriptionFrom(dataSentence(name, data.stats)) : `${FORMAT_INTRO_COPY[formatKey]} See the most wanted, traded and available ${label} cards on TradeMagic.`;
  const searchHref = `/search?format=${formatKey}&formatLegality=legal`;

  const tabs: TabSpec[] = data
    ? [
        {
          key: 'wanted',
          label: 'Most Wanted',
          rows: data.mostWanted,
          countLabel: (n) => `${n.toLocaleString()} ${n === 1 ? 'trader wants it' : 'traders want it'}`,
          emptyTitle: `No ${name} cards on want lists yet`,
          emptyBody: 'Add the cards you are looking for and this list fills in overnight.',
        },
        {
          key: 'traded',
          label: 'Most Traded',
          note: 'last 90 days',
          rows: data.mostTraded,
          countLabel: (n) => `${n.toLocaleString()} ${n === 1 ? 'trade' : 'trades'}`,
          emptyTitle: `No completed ${name} trades in the last 90 days`,
          emptyBody: 'Send a card from this format and it will show up here once the trade completes.',
        },
        {
          key: 'available',
          label: 'Most Available',
          rows: data.mostAvailable,
          countLabel: (n) => `${n.toLocaleString()} ${n === 1 ? 'available' : 'available'}`,
          emptyTitle: `No ${name} cards listed yet`,
          emptyBody: 'List the cards you are willing to trade and they count here.',
        },
      ]
    : [];

  const stat = (value: number | undefined) => (value === undefined ? '–' : value.toLocaleString());

  return (
    <>
      <SEOHelmet
        title={`Most Wanted ${label} Cards to Trade | TradeMagic`}
        description={description}
        canonical={canonical}
        type="website"
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: `${SITE}/` },
          { name: 'Formats', url: `${SITE}/formats` },
          { name: label, url: canonical },
        ]}
      />
      {data && data.mostWanted.length > 0 && <StructuredData data={mostWantedItemList(name, data.mostWanted, canonical)} />}

      <div className="formats-layout">
        {isMobile && isMobileMenuOpen && (
          <div className="formats-mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" />
        )}
        <div className="formats-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/formats', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={() => { if (!currentUser) setShowLoginModal(true); }}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="formats-main">
            <TopNavigationBar
              title={label}
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={() => setIsMobileMenuOpen((o) => !o)}
            />
            <div className="formats-container">
              <nav className="formats-breadcrumb" aria-label="Breadcrumb">
                <Link to="/formats">Formats</Link>
                <span aria-hidden="true"> / </span>
                <span>{label}</span>
              </nav>

              <header className="formats-detail-header">
                <div className="formats-detail-header__text">
                  <h1 className="formats-title">Most wanted {label} cards</h1>
                  <p className="formats-intro">{intro}</p>
                </div>
                <div className="formats-ctas">
                  <Link to={searchHref} className="formats-btn formats-btn--primary">Search {label} cards</Link>
                  {currentUser ? (
                    <Link to="/my-cards/have-list" className="formats-btn formats-btn--ghost">Add cards you have</Link>
                  ) : (
                    <button type="button" className="formats-btn formats-btn--ghost" onClick={() => setShowLoginModal(true)}>
                      Add cards you have
                    </button>
                  )}
                </div>
              </header>

              {isLoading ? (
                <p className="formats-muted">Loading…</p>
              ) : error || !data ? (
                <p className="formats-muted">Trade data for this format is not available right now.</p>
              ) : (
                <>
                  <dl className="formats-stats" data-testid="formats-stats">
                    <div className="formats-stat">
                      <dd>{stat(data.stats.wantUsers)}</dd>
                      <dt>traders want {name} cards</dt>
                    </div>
                    <div className="formats-stat">
                      <dd>{stat(data.stats.tradeCount90d)}</dd>
                      <dt>completed trades in the last 90 days</dt>
                    </div>
                    <div className="formats-stat">
                      <dd>{stat(data.stats.haveCopies)}</dd>
                      <dt>copies available to trade</dt>
                    </div>
                  </dl>

                  <nav className="formats-pills" aria-label="Other formats">
                    {FORMAT_PAGE_KEYS.map((key) => (
                      <Link
                        key={key}
                        to={`/formats/${key}`}
                        className={`formats-pill${key === formatKey ? ' formats-pill--current' : ''}`}
                        aria-current={key === formatKey ? 'page' : undefined}
                      >
                        {FORMAT_PAGE_LABELS[key]}
                      </Link>
                    ))}
                  </nav>

                  <section className="formats-panel">
                    <div className="formats-tabbar">
                      <div role="tablist" aria-label={`${label} lists`} className="formats-tabs">
                        {tabs.map((tab) => (
                          <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            id={`format-tab-${tab.key}`}
                            aria-selected={activeTab === tab.key}
                            aria-controls={`format-tabpanel-${tab.key}`}
                            className={`formats-tab${activeTab === tab.key ? ' formats-tab--active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                          >
                            {tab.label}
                            {tab.note && <span className="formats-tab__note"> {tab.note}</span>}
                          </button>
                        ))}
                      </div>
                      <p className="formats-updated">
                        Updated <time dateTime={data.computedAt}>{formatUpdated(data.computedAt)}</time>
                      </p>
                    </div>
                    {tabs.map((tab) => (
                      <RankedTabPanel key={tab.key} tab={tab} active={activeTab === tab.key} label={label} />
                    ))}
                  </section>
                </>
              )}
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername=""
      />
    </>
  );
};

export default FormatDetailPage;
